import { useEffect, useRef, useState } from "react";
import { ArrowDown, ArrowUp, CheckSquare, DownloadSimple, FileZip, Plus, Square, Trash, UploadSimple } from "@phosphor-icons/react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { downloadBlob, formatBytes, STATUS_LABEL, STATUS_CHIP } from "@/lib/bootcampFiles";

export function BootcampsAdminSection() {
  const [active, setActive] = useState(1);
  const [view, setView] = useState("content");
  return (
    <div className="fade-up space-y-6" data-testid="admin-bootcamps-section">
      <div>
        <p className="label-uppercase" style={{ color: "var(--muted)" }}>Mentorship</p>
        <h2 className="text-2xl font-bold" style={{ color: "var(--foreground)" }}>Bootcamps</h2>
        <p className="text-sm" style={{ color: "var(--muted)" }}>Publish tasks and workbooks, review submissions and tick completion.</p>
      </div>
      <div className="flex flex-wrap gap-3 items-center justify-between">
        <div className="status-filter" data-testid="admin-bootcamp-tabs">
          {[1, 2, 3, 4].map((n) => (
            <button key={n} className={`status-pill ${active === n ? "is-active" : ""}`} onClick={() => setActive(n)} data-testid={`admin-bootcamp-tab-${n}`}>Bootcamp {n}</button>
          ))}
        </div>
        <div className="status-filter">
          {[["content", "Content & tasks"], ["participants", "Participants"]].map(([k, l]) => (
            <button key={k} className={`status-pill ${view === k ? "is-active" : ""}`} onClick={() => setView(k)} data-testid={`admin-bootcamp-view-${k}`}>{l}</button>
          ))}
        </div>
      </div>
      {view === "content" ? <BootcampContentEditor key={active} number={active} /> : <BootcampParticipants key={active} number={active} />}
    </div>
  );
}

function BootcampContentEditor({ number }) {
  const [bc, setBc] = useState(null);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const fileRef = useRef(null);

  const load = async () => {
    const { data } = await api.get(`/admin/bootcamps`);
    setBc(data.bootcamps.find((b) => b.number === number));
  };
  useEffect(() => { load(); }, [number]); // eslint-disable-line react-hooks/exhaustive-deps

  const save = async (patch) => {
    setSaving(true); setErr(""); setMsg("");
    try {
      const { data } = await api.put(`/admin/bootcamps/${number}`, patch);
      // Partial save: only take back what we sent (tasks get server ids); keep other local edits.
      setBc((b) => ({ ...b, ...(patch.tasks !== undefined ? { tasks: data.tasks } : {}), resources: data.resources }));
      setMsg("Saved");
      setTimeout(() => setMsg(""), 1500);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  const setTasks = (tasks) => setBc((b) => ({ ...b, tasks }));
  const moveTask = (i, d) => {
    const t = [...bc.tasks]; const j = i + d;
    if (j < 0 || j >= t.length) return;
    [t[i], t[j]] = [t[j], t[i]]; setTasks(t);
  };

  const uploadResource = async (file) => {
    if (!file) return;
    if (file.size > 200 * 1024 * 1024) { setErr("Resources can be at most 200 MB each."); return; }
    setSaving(true); setErr("");
    try {
      const form = new FormData(); form.append("file", file);
      const { data } = await api.post(`/admin/bootcamps/${number}/resources`, form, { timeout: 600000 });
      setBc(data);
    } catch (e) {
      setErr(formatApiErrorDetail(e.response?.data?.detail) || "Upload failed");
    } finally { setSaving(false); }
  };

  const removeResource = async (r) => {
    if (!window.confirm(`Remove ${r.filename}?`)) return;
    const { data } = await api.delete(`/admin/bootcamps/${number}/resources/${r.id}`);
    setBc(data);
  };

  if (!bc) return <div className="py-10 flex justify-center"><span className="spinner" /></div>;
  return (
    <div className="space-y-5" data-testid="admin-bootcamp-editor">
      <div className="grid-frame p-5 space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <label className="flex items-center gap-2 text-sm" style={{ color: "var(--foreground)" }}>
            <input type="checkbox" checked={!!bc.published} onChange={(e) => { const v = e.target.checked; setBc((b) => ({ ...b, published: v })); save({ published: v }); }} data-testid="admin-bootcamp-published" />
            {bc.published ? "Published — visible to participants" : "Draft — hidden from participants"}
          </label>
          <span className="text-xs" style={{ color: "var(--muted)" }}>{saving ? "Saving…" : msg}</span>
        </div>
        <input className="kb-input w-full" value={bc.title || ""} onChange={(e) => setBc({ ...bc, title: e.target.value })} onBlur={() => save({ title: bc.title })} placeholder={`Bootcamp ${number}`} data-testid="admin-bootcamp-title" />
        <textarea className="kb-input w-full" rows={4} value={bc.notes || ""} onChange={(e) => setBc({ ...bc, notes: e.target.value })} onBlur={() => save({ notes: bc.notes })} placeholder="Speaker notes / description shown to participants" data-testid="admin-bootcamp-notes" />
        {err && <p className="error-banner" data-testid="admin-bootcamp-error">{err}</p>}
      </div>

      <div className="grid-frame p-5 space-y-3">
        <div className="flex items-center justify-between">
          <p className="label-uppercase" style={{ color: "var(--muted)" }}>Checklist tasks</p>
          <button className="btn btn-sm" onClick={() => setTasks([...(bc.tasks || []), { id: null, title: "", instructions: "" }])} data-testid="admin-task-add"><Plus size={14} /> Add task</button>
        </div>
        {(bc.tasks || []).map((t, i) => (
          <div key={t.id || `new-${i}`} className="niche-card space-y-2" data-testid={`admin-task-row-${i}`}>
            <div className="flex gap-2 items-center">
              <span className="text-xs w-5" style={{ color: "var(--muted)" }}>{i + 1}.</span>
              <input className="kb-input flex-1" value={t.title} placeholder="Task title" onChange={(e) => setTasks(bc.tasks.map((x, k) => (k === i ? { ...x, title: e.target.value } : x)))} data-testid={`admin-task-title-${i}`} />
              <button className="btn btn-ghost btn-sm" onClick={() => moveTask(i, -1)} aria-label="Move up"><ArrowUp size={14} /></button>
              <button className="btn btn-ghost btn-sm" onClick={() => moveTask(i, 1)} aria-label="Move down"><ArrowDown size={14} /></button>
              <button className="btn btn-ghost btn-sm" onClick={() => setTasks(bc.tasks.filter((_, k) => k !== i))} aria-label="Remove task" data-testid={`admin-task-remove-${i}`}><Trash size={14} /></button>
            </div>
            <textarea className="kb-input w-full" rows={2} value={t.instructions} placeholder="Instructions" onChange={(e) => setTasks(bc.tasks.map((x, k) => (k === i ? { ...x, instructions: e.target.value } : x)))} data-testid={`admin-task-instructions-${i}`} />
          </div>
        ))}
        <button className="btn btn-accent btn-sm" disabled={saving} onClick={() => save({ tasks: (bc.tasks || []).filter((t) => t.title.trim()) })} data-testid="admin-tasks-save">Save tasks</button>
      </div>

      <div className="grid-frame p-5 space-y-3">
        <div className="flex items-center justify-between">
          <p className="label-uppercase" style={{ color: "var(--muted)" }}>Resources (workbooks, files · up to 200 MB each)</p>
          <button className="btn btn-sm" onClick={() => fileRef.current?.click()} disabled={saving} data-testid="admin-resource-upload"><UploadSimple size={14} /> Upload</button>
          <input ref={fileRef} type="file" className="hidden" data-testid="admin-resource-file-input" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; uploadResource(f); }} />
        </div>
        {(bc.resources || []).length === 0 && <p className="text-sm" style={{ color: "var(--muted)" }}>No resources yet.</p>}
        <ul className="divide-y" style={{ borderColor: "var(--border)" }}>
          {(bc.resources || []).map((r) => (
            <li key={r.id} className="flex items-center justify-between gap-3 py-2 text-sm" data-testid={`admin-resource-${r.id}`}>
              <span className="truncate" style={{ color: "var(--foreground)" }}>{r.filename} <span style={{ color: "var(--muted)" }}>· {formatBytes(r.size)}</span></span>
              <div className="flex gap-1">
                <button className="btn btn-ghost btn-sm" onClick={() => downloadBlob(`/bootcamps/${number}/resources/${r.id}/download`, r.filename)}><DownloadSimple size={14} /></button>
                <button className="btn btn-ghost btn-sm" onClick={() => removeResource(r)} data-testid={`admin-resource-remove-${r.id}`}><Trash size={14} /></button>
              </div>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function BootcampParticipants({ number }) {
  const [rows, setRows] = useState(null);
  const [status, setStatus] = useState("all");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);

  const load = async () => {
    const { data } = await api.get(`/admin/bootcamps/${number}/participants`, { params: { status, q } });
    setRows(data.participants);
  };
  useEffect(() => { load(); }, [number, status, q]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="space-y-4" data-testid="admin-bootcamp-participants">
      <div className="users-toolbar">
        <div className="admin-search w-full max-w-md"><input placeholder="Search by name or email…" value={q} onChange={(e) => setQ(e.target.value)} data-testid="admin-bootcamp-search" /></div>
        <div className="status-filter">
          {[["all", "All"], ["not_started", "Not started"], ["submitted", "Submitted"], ["completed", "Completed"]].map(([k, l]) => (
            <button key={k} className={`status-pill ${status === k ? "is-active" : ""}`} onClick={() => setStatus(k)} data-testid={`admin-bootcamp-filter-${k}`}>{l}</button>
          ))}
        </div>
      </div>
      {!rows ? <div className="py-10 flex justify-center"><span className="spinner" /></div> : (
        <div className="grid-frame overflow-x-auto">
          <table className="adm-table" data-testid="admin-bootcamp-table">
            <thead><tr><th>Participant</th><th>Files</th><th>Last upload</th><th>Tasks ticked</th><th>Status</th><th></th></tr></thead>
            <tbody>
              {rows.length === 0 && <tr><td colSpan={6} className="text-center py-6" style={{ color: "var(--muted)" }}>No participants match.</td></tr>}
              {rows.map((r) => (
                <tr key={r.user_id} data-testid={`admin-bootcamp-row-${r.user_id}`}>
                  <td><div className="font-medium">{r.name}</div><div className="text-xs" style={{ color: "var(--muted)" }}>{r.email}</div></td>
                  <td>{r.files} <span className="text-xs" style={{ color: "var(--muted)" }}>· {formatBytes(r.total_bytes)}</span></td>
                  <td className="text-xs">{r.last_upload_at ? new Date(r.last_upload_at).toLocaleString() : "—"}</td>
                  <td>{r.ticked}/{r.tasks}</td>
                  <td><span className={STATUS_CHIP[r.status]}>{STATUS_LABEL[r.status]}</span></td>
                  <td><button className="btn btn-ghost btn-sm" onClick={() => setOpen(r)} data-testid={`admin-bootcamp-review-${r.user_id}`}>Review</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {open && (
        <div className="grid-frame p-5" data-testid="admin-bootcamp-review-panel">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold" style={{ color: "var(--foreground)" }}>{open.name} <span className="text-xs font-normal" style={{ color: "var(--muted)" }}>{open.email}</span></h3>
            <button className="btn btn-ghost btn-sm" onClick={() => setOpen(null)} data-testid="admin-bootcamp-review-close">Close</button>
          </div>
          <ParticipantReview number={number} userId={open.user_id} onChanged={load} />
        </div>
      )}
    </div>
  );
}

export function ParticipantReview({ number, userId, onChanged }) {
  const [d, setD] = useState(null);
  const [remark, setRemark] = useState("");
  const [err, setErr] = useState("");

  const load = async () => {
    const { data } = await api.get(`/admin/bootcamps/${number}/participants/${userId}`);
    setD(data); setRemark(data.progress.remark || "");
  };
  useEffect(() => { load(); }, [number, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  const saveProgress = async (patch) => {
    setErr("");
    try {
      const { data } = await api.put(`/admin/bootcamps/${number}/participants/${userId}/progress`, patch);
      setD((x) => ({ ...x, progress: data.progress, status: data.status }));
      onChanged?.();
    } catch (e) { setErr(formatApiErrorDetail(e.response?.data?.detail) || "Could not save"); }
  };
  const toggle = (tid) => {
    const cur = new Set(d.progress.ticked_task_ids || []);
    cur.has(tid) ? cur.delete(tid) : cur.add(tid);
    saveProgress({ ticked_task_ids: [...cur] });
  };

  if (!d) return <div className="py-6 flex justify-center"><span className="spinner" /></div>;
  const tasks = d.bootcamp.tasks || [];
  return (
    <div className="space-y-4" data-testid={`participant-review-${number}`}>
      <div className="flex items-center gap-3 flex-wrap">
        <span className={STATUS_CHIP[d.status]} data-testid="participant-review-status">{STATUS_LABEL[d.status]}</span>
        {d.submissions.length > 0 && (
          <button className="btn btn-sm" onClick={() => downloadBlob(`/admin/bootcamps/${number}/participants/${userId}/zip`, `bootcamp${number}.zip`)} data-testid="participant-review-zip"><FileZip size={14} /> Download all as zip</button>
        )}
      </div>
      <div>
        <p className="label-uppercase mb-2" style={{ color: "var(--muted)" }}>Checklist</p>
        {tasks.length === 0 && <p className="text-sm" style={{ color: "var(--muted)" }}>No tasks defined for this bootcamp yet.</p>}
        {tasks.map((t, i) => {
          const on = (d.progress.ticked_task_ids || []).includes(t.id);
          return (
            <button key={t.id} type="button" className="flex items-start gap-2 text-left text-sm py-1 w-full" onClick={() => toggle(t.id)} data-testid={`participant-tick-${t.id}`} style={{ color: "var(--foreground)" }}>
              <span style={{ color: on ? "var(--lime)" : "var(--muted)" }}>{on ? <CheckSquare size={18} weight="fill" /> : <Square size={18} />}</span>
              <span>{i + 1}. {t.title}</span>
            </button>
          );
        })}
      </div>
      <div>
        <p className="label-uppercase mb-2" style={{ color: "var(--muted)" }}>Submissions ({d.submissions.length})</p>
        {d.submissions.length === 0 && <p className="text-sm" style={{ color: "var(--muted)" }}>Nothing submitted yet.</p>}
        <ul className="divide-y" style={{ borderColor: "var(--border)" }}>
          {d.submissions.map((s) => (
            <li key={s.id} className="flex items-center justify-between gap-3 py-2 text-sm" data-testid={`review-submission-${s.id}`}>
              <div className="min-w-0">
                <p className="truncate" style={{ color: "var(--foreground)" }}>{s.filename} <span style={{ color: "var(--muted)" }}>· {formatBytes(s.size)}</span></p>
                <p className="text-xs" style={{ color: "var(--muted)" }}>{new Date(s.uploaded_at).toLocaleString()}{s.note ? ` · ${s.note}` : ""}{s.task_id ? ` · ${tasks.find((t) => t.id === s.task_id)?.title || "task"}` : ""}</p>
              </div>
              <button className="btn btn-ghost btn-sm" onClick={() => downloadBlob(`/bootcamps/${number}/submissions/${s.id}/download`, s.filename)} data-testid={`review-download-${s.id}`}><DownloadSimple size={14} /></button>
            </li>
          ))}
        </ul>
      </div>
      <div>
        <p className="label-uppercase mb-1" style={{ color: "var(--muted)" }}>Admin remark (not visible to participant)</p>
        <textarea className="kb-input w-full" rows={2} value={remark} onChange={(e) => setRemark(e.target.value)} onBlur={() => remark !== (d.progress.remark || "") && saveProgress({ remark })} data-testid="participant-remark" />
      </div>
      {err && <p className="error-banner">{err}</p>}
    </div>
  );
}

export function UserBootcampsTab({ user }) {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(1);
  const load = async () => {
    const { data } = await api.get(`/admin/users/${user.id}/bootcamps`);
    setData(data.bootcamps);
  };
  useEffect(() => { load(); }, [user.id]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!data) return <div className="py-10 flex justify-center"><span className="spinner" /></div>;
  return (
    <div className="space-y-3" data-testid="drawer-bootcamps">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        {data.map((b) => (
          <button key={b.number} className={`niche-card text-left ${open === b.number ? "is-check" : ""}`} onClick={() => setOpen(b.number)} data-testid={`drawer-bootcamp-${b.number}`}>
            <p className="text-xs font-semibold" style={{ color: "var(--foreground)" }}>Bootcamp {b.number}</p>
            <p className="text-xs" style={{ color: "var(--muted)" }}>{b.submissions.length} file{b.submissions.length === 1 ? "" : "s"} · {(b.progress.ticked_task_ids || []).length}/{b.tasks.length}</p>
            <span className={`${STATUS_CHIP[b.status]} mt-1`} style={{ fontSize: "0.6rem" }}>{STATUS_LABEL[b.status]}</span>
          </button>
        ))}
      </div>
      <ParticipantReview key={open} number={open} userId={user.id} onChanged={load} />
    </div>
  );
}
